import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createMidiOutputEngine,
  createPlaybackController,
  createSoundfontEngine,
  getMidiOutputPort,
  tickToSeconds,
  type SoundfontSource,
} from "@midi-fantom-utils/player-core";
import { buildPlaybackData, type Pattern } from "@midi-fantom-utils/midi-util";
import type { MidiPortSelection, SoundOutput } from "../config.js";

export type PatternMiniPlayerProps = {
  pattern: Pattern;
  source: SoundfontSource;
  output: SoundOutput;
  midiPort: MidiPortSelection | null;
};

/**
 * Мини-плеер для одного паттерна. Кнопка `▶`/`■` запускает/останавливает
 * воспроизведение через собственный экземпляр движка (`createSoundfontEngine`
 * в SF2-режиме или `createMidiOutputEngine` в MIDI-режиме) +
 * `createPlaybackController`. SF2-режим шарит `AudioContext` через модульный
 * ref, чтобы не плодить новые контексты на каждое нажатие ▶.
 *
 * Q10: допускается параллельное воспроизведение нескольких паттернов
 * одновременно с таймлайн-плеером — каждый мини-плеер создаёт свой
 * engine, но звучит через общий `masterGain`.
 */

// Один глобальный AudioContext + masterGain на сессию (только SF2-режим).
// Автополитика браузера ограничивает число контекстов; шеринг между
// мини-плеерами и TimelinePlayer безопасен.
let sharedAudioCtx: AudioContext | null = null;
let sharedMasterGain: GainNode | null = null;

const ensureSharedContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  if (sharedAudioCtx) return sharedAudioCtx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  const ctx = new Ctor();
  const gain = ctx.createGain();
  gain.gain.value = 0.8;
  gain.connect(ctx.destination);
  sharedAudioCtx = ctx;
  sharedMasterGain = gain;
  return ctx;
};

type Status = "idle" | "playing" | "ended" | "error";

const sourceKey = (source: SoundfontSource): string => `${source.kind}:${source.url}`;

export const PatternMiniPlayer = ({
  pattern,
  source,
  output,
  midiPort,
}: PatternMiniPlayerProps) => {
  const [audioCtx, setAudioCtx] = useState<AudioContext | null>(sharedAudioCtx);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const sf2EngineRef = useRef<ReturnType<typeof createSoundfontEngine> | null>(null);
  const midiEngineRef = useRef<ReturnType<typeof createMidiOutputEngine> | null>(null);
  const controllerRef = useRef<ReturnType<typeof createPlaybackController> | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  const playback = useMemo(() => buildPlaybackData(pattern.midi), [pattern.midi]);
  const keyRef = useRef<string>("");

  const playbackKey =
    output === "sf2"
      ? `sf2:${sourceKey(source)}`
      : `midi:${midiPort?.id ?? ""}:${midiPort?.name ?? ""}`;

  useEffect(() => {
    return () => {
      unsubRef.current?.();
      unsubRef.current = null;
      controllerRef.current?.dispose();
      controllerRef.current = null;
      sf2EngineRef.current?.dispose();
      sf2EngineRef.current = null;
      midiEngineRef.current?.dispose();
      midiEngineRef.current = null;
    };
  }, []);

  // Смена режима вывода / шрифта / порта: старые движки держали бы прежний
  // источник. Диспоузим их — следующий ▶ создаст с новым.
  useEffect(() => {
    if (keyRef.current === playbackKey) return;
    keyRef.current = playbackKey;
    unsubRef.current?.();
    unsubRef.current = null;
    controllerRef.current?.dispose();
    controllerRef.current = null;
    sf2EngineRef.current?.dispose();
    sf2EngineRef.current = null;
    midiEngineRef.current?.dispose();
    midiEngineRef.current = null;
    setStatus("idle");
    setError(null);
  }, [playbackKey]);

  const handlePlay = useCallback(async () => {
    if (output === "midi") {
      const port = await getMidiOutputPort(midiPort);
      if (!port) {
        setError("MIDI-порт недоступен — подключите устройство в настройках");
        setStatus("error");
        return;
      }
      if (!midiEngineRef.current) {
        midiEngineRef.current = createMidiOutputEngine({
          port,
          notes: playback.notes,
        });
      } else {
        midiEngineRef.current.setInput({ port, notes: playback.notes });
      }

      if (!controllerRef.current) {
        const controller = createPlaybackController({
          clock: {
            now: () => performance.now() / 1000,
            resume: () => Promise.resolve(),
          },
          getNotes: () => playback.notes,
          tempos: playback.tempos,
          ppq: playback.ppq,
          onScheduleNote: (note, when) => {
            midiEngineRef.current?.playNote(
              note,
              when,
              Math.max(
                0.05,
                tickToSeconds(note.durationTicks, playback.tempos, playback.ppq)
              )
            );
          },
          onStopAll: () => midiEngineRef.current?.stopAll(),
        });
        const unsub = controller.subscribe(() => {
          setStatus(controller.getState().status as Status);
        });
        unsubRef.current = unsub;
        controllerRef.current = controller;
      }

      setError(null);
      controllerRef.current.play();
      return;
    }

    // SF2-режим (как раньше).
    let ctx = audioCtx ?? ensureSharedContext();
    if (!ctx) {
      setError("Web Audio не поддерживается");
      setStatus("error");
      return;
    }
    if (ctx.state === "suspended") await ctx.resume();
    if (!audioCtx) setAudioCtx(ctx);

    if (!sf2EngineRef.current) {
      const engine = createSoundfontEngine({
        audioCtx: ctx,
        source,
        notes: playback.notes,
        destination: sharedMasterGain ?? undefined,
        defer: true,
      });
      sf2EngineRef.current = engine;
      engine.setInput({
        source,
        notes: playback.notes,
        destination: sharedMasterGain ?? undefined,
      });
    }

    if (!controllerRef.current) {
      const controller = createPlaybackController({
        audioCtx: ctx,
        getNotes: () => playback.notes,
        tempos: playback.tempos,
        ppq: playback.ppq,
        onScheduleNote: (note, when) => {
          sf2EngineRef.current?.playNote(
            note,
            when,
            Math.max(
              0.05,
              tickToSeconds(note.durationTicks, playback.tempos, playback.ppq)
            )
          );
        },
        onStopAll: () => sf2EngineRef.current?.stopAll(),
      });
      const unsub = controller.subscribe(() => {
        setStatus(controller.getState().status as Status);
      });
      unsubRef.current = unsub;
      controllerRef.current = controller;
    }

    setError(null);
    controllerRef.current.play();
  }, [audioCtx, source, playback, output, midiPort]);

  const handleStop = useCallback(() => {
    controllerRef.current?.stop();
  }, []);

  const isPlaying = status === "playing";

  return (
    <button
      type="button"
      className="btn"
      style={{ padding: "2px 6px", fontSize: 11 }}
      onClick={isPlaying ? handleStop : handlePlay}
      aria-label={isPlaying ? "Стоп" : "Воспроизвести"}
      title={error ?? undefined}
    >
      {isPlaying ? "■" : "▶"}
    </button>
  );
};